/*
 * Discounts & Campaigns — storefront hydration.
 *
 * Reads ONE Supabase row (app_settings, key=campaign_config) — the same row
 * the React admin writes and the React builder reads — and hydrates two
 * storefront surfaces from a single fetch:
 *
 *   1. Site banner  [data-campaign-site-banner]  — slim bar above page content
 *   2. PDP label    [data-campaign-pdp-label]     — clickable discount label
 *
 * Both render hidden in Liquid; this script reveals + fills them, so there's
 * no flash when a campaign is inactive. Fails silent (matches the
 * personalisation-* sections' Supabase pattern).
 *
 * The discount code is applied via Shopify's native /discount/<CODE> endpoint,
 * which attaches the code to the cart/session (pre-applied at checkout) and
 * redirects back to the current page.
 */
(function () {
  var SUPA_URL = 'https://oquugatwdfrfnnxjbldg.supabase.co';
  var SUPA_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im9xdXVnYXR3ZGZyZm5ueGpibGRnIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzQ4OTgzODksImV4cCI6MjA5MDQ3NDM4OX0.7ixhYEo1eztKqV1oX2MAnHQFuuGPrC6dZKT_krsunTY';

  var siteBanner = document.querySelector('[data-campaign-site-banner]');
  var pdpLabel = document.querySelector('[data-campaign-pdp-label]');

  // Nothing to hydrate on this page.
  if (!siteBanner && !pdpLabel) return;

  function show(el) {
    if (!el) return;
    el.classList.remove('hidden');
  }

  // Render admin text with lightweight **bold** support. HTML is escaped
  // first (so it's safe even though only admins can write the value via
  // RLS), then **...** is turned into <strong>. Lets a campaign emphasise
  // the discount, e.g. "**15% off** with code **SAVE15**".
  function renderRichText(el, raw) {
    var esc = raw
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    el.innerHTML = esc.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  }

  function applySiteBanner(cfg) {
    if (!siteBanner || !cfg || !cfg.enabled) return;
    var text = (cfg.text || '').trim();
    if (!text) return;
    var textEl = siteBanner.querySelector('[data-campaign-site-banner-text]') || siteBanner;
    renderRichText(textEl, text);
    show(siteBanner);
  }

  // Tapping the label hands off to Shopify's /discount/<CODE> route, which
  // sets the discount on the cart/session and RELOADS the page. So an in-page
  // "Coupon applied" swap would be wiped by that reload. We record the code we
  // applied in sessionStorage BEFORE navigating; when the page comes back we
  // render the label in its applied state (tick + confirmation) instead of the
  // tap-to-save CTA. sessionStorage (not local) so it tracks the current
  // browsing session, matching the lifetime of Shopify's discount cookie.
  // Fails silent if storage is blocked (private mode) -- the discount is still
  // really applied, we just fall back to showing the CTA again.
  /*
   * Campaign colour. The admin sets ONE hex in Discounts & Campaigns; the pill's
   * background, hover, border and text are derived from it, so a bad pick can't
   * produce unreadable text (the text is darkened until it clears WCAG AA).
   *
   * ⚠️ MIRROR of src/lib/campaignPillColor.ts in the React app — the admin
   * preview and this pill must agree. Change one, change the other. Different
   * deployments, no shared bundler, hence the copy.
   *
   * No hex (or an unparseable one) returns null and the Liquid indigo classes
   * stand, which match the sitewide discount banner.
   */
  function parseHex(input) {
    if (!input) return null;
    var s = String(input).trim().toLowerCase();
    if (s.charAt(0) !== '#') s = '#' + s;
    if (/^#[0-9a-f]{3}$/.test(s)) {
      s = '#' + s.charAt(1) + s.charAt(1) + s.charAt(2) + s.charAt(2) + s.charAt(3) + s.charAt(3);
    }
    if (!/^#[0-9a-f]{6}$/.test(s)) return null;
    return {
      r: parseInt(s.slice(1, 3), 16),
      g: parseInt(s.slice(3, 5), 16),
      b: parseInt(s.slice(5, 7), 16)
    };
  }

  function toHex(c) {
    function h(n) {
      var v = Math.round(Math.min(255, Math.max(0, n))).toString(16);
      return v.length === 1 ? '0' + v : v;
    }
    return '#' + h(c.r) + h(c.g) + h(c.b);
  }

  function mix(c, target, t) {
    return {
      r: c.r + (target.r - c.r) * t,
      g: c.g + (target.g - c.g) * t,
      b: c.b + (target.b - c.b) * t
    };
  }

  var WHITE = { r: 255, g: 255, b: 255 };
  var BLACK = { r: 0, g: 0, b: 0 };

  function luminance(c) {
    function channel(v) {
      var s = v / 255;
      return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    }
    return 0.2126 * channel(c.r) + 0.7152 * channel(c.g) + 0.0722 * channel(c.b);
  }

  function contrast(a, b) {
    var la = luminance(a);
    var lb = luminance(b);
    return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
  }

  function derivePillColors(hex) {
    var base = parseHex(hex);
    if (!base) return null;

    var bg = mix(WHITE, base, 0.07);
    var bgHover = mix(WHITE, base, 0.13);
    var border = mix(WHITE, base, 0.45);

    var text = mix(base, BLACK, 0.25);
    for (var i = 0; i < 12 && contrast(text, bg) < 4.5; i++) {
      text = mix(text, BLACK, 0.12);
    }

    return { bg: toHex(bg), bgHover: toHex(bgHover), border: toHex(border), text: toHex(text) };
  }

  // Inline styles beat the Tailwind utilities, so the indigo classes are REMOVED
  // rather than left to fight them -- in particular hover:bg-indigo-100, which an
  // inline background would silently win against, leaving the pill hover-dead.
  // Hover is re-bound in JS below, but only while the pill is still clickable.
  var INDIGO_CLASSES = [
    'border-indigo-300',
    'bg-indigo-50',
    'text-indigo-700',
    'hover:bg-indigo-100',
    'focus-visible:outline-indigo-400'
  ];

  function paintPdpLabel(colors) {
    for (var i = 0; i < INDIGO_CLASSES.length; i++) pdpLabel.classList.remove(INDIGO_CLASSES[i]);
    pdpLabel.style.backgroundColor = colors.bg;
    pdpLabel.style.borderColor = colors.border;
    pdpLabel.style.color = colors.text;
    // Only ever visible under focus-visible, which is what turns the outline on.
    pdpLabel.style.outlineColor = colors.border;
  }

  function bindPdpHover(colors) {
    pdpLabel.addEventListener('mouseenter', function () {
      pdpLabel.style.backgroundColor = colors.bgHover;
    });
    pdpLabel.addEventListener('mouseleave', function () {
      pdpLabel.style.backgroundColor = colors.bg;
    });
  }

  var APPLIED_KEY = 'aj_campaign_applied_code';
  var TICK_SVG =
    '<svg data-campaign-pdp-tick aria-hidden="true" width="16" height="16" ' +
    'viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M4.5 10.5l3.5 3.5 7.5-8" stroke="currentColor" stroke-width="2.5" ' +
    'stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function sessionGet(k) { try { return window.sessionStorage.getItem(k); } catch (e) { return null; } }
  function sessionSet(k, v) { try { window.sessionStorage.setItem(k, v); } catch (e) {} }

  // Swap the label into its success state: tick icon + confirmation text, no
  // longer clickable.
  function showPdpApplied(textEl, appliedText) {
    if (textEl) textEl.textContent = appliedText;
    if (!pdpLabel.querySelector('[data-campaign-pdp-tick]')) {
      pdpLabel.insertAdjacentHTML('afterbegin', TICK_SVG);
    }
    pdpLabel.disabled = true;
    pdpLabel.style.cursor = 'default';
    pdpLabel.setAttribute('aria-disabled', 'true');
    pdpLabel.classList.remove('hover:bg-indigo-100');
  }

  function applyPdpLabel(cfg) {
    if (!pdpLabel || !cfg || !cfg.enabled) return;
    var text = (cfg.text || '').trim();
    if (!text) return;

    var textEl = pdpLabel.querySelector('[data-campaign-pdp-label-text]') || pdpLabel;
    var code = (cfg.code || '').trim();
    var appliedText = (cfg.applied_text || 'Coupon applied').trim();

    // Admin-set campaign colour, if any. Painted before the state branches so
    // the applied state inherits it too.
    var colors = derivePillColors(cfg.color);
    if (colors) paintPdpLabel(colors);

    if (code && sessionGet(APPLIED_KEY) === code) {
      // Returned from /discount/<CODE> (or a later page view this session):
      // the code is on the cart/session, so confirm it's applied.
      showPdpApplied(textEl, appliedText);
    } else {
      renderRichText(textEl, text);
      if (code) {
        if (colors) bindPdpHover(colors);
        pdpLabel.addEventListener('click', function () {
          // Record BEFORE navigating -- the /discount route reloads the page.
          sessionSet(APPLIED_KEY, code);
          var redirect = window.location.pathname + window.location.search;
          window.location.href =
            '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(redirect);
        });
      } else {
        // No code: show the message but make it non-interactive.
        pdpLabel.disabled = true;
        pdpLabel.style.cursor = 'default';
      }
    }

    pdpLabel.classList.remove('hidden');
    pdpLabel.classList.add('inline-flex');
  }

  fetch(SUPA_URL + '/rest/v1/app_settings?key=eq.campaign_config&select=value', {
    headers: { apikey: SUPA_KEY, Authorization: 'Bearer ' + SUPA_KEY }
  })
    .then(function (r) { return r.ok ? r.json() : Promise.reject(r.status); })
    .then(function (rows) {
      var value = Array.isArray(rows) && rows[0] ? rows[0].value : null;
      if (!value || typeof value !== 'object') return;
      applySiteBanner(value.site_banner);
      applyPdpLabel(value.pdp_label);
    })
    .catch(function (e) { console.warn('[aj-campaigns]', e); });
})();
